import React, { useCallback, useEffect, useState } from 'react';
import DataTable from './DataTable';
import useMeanTimeToResolveDetailed, { getMeanTimeToResolveDetailedScope } from '../../hooks/useMeanTimeToResolveDetailed';
import { formatMttrText } from '../../utils/formatMttr';
import { JIRA_BASE } from './constants';

const PAGE_SIZE = 25;

const COLUMNS = [
  {
    key: 'ticket',
    label: 'Ticket',
    render: r => (
      <a href={`${JIRA_BASE}/${r.key}`} target="_blank" rel="noopener noreferrer">
        {r.summary ? `[${r.key}] ${r.summary}` : r.key}
      </a>
    ),
  },
  { key: 'mttrHours', label: 'MTTR', render: r => formatMttrText(r.mttrHours) },
];

// Per-incident MTTR table (QA-7149), paginated server-side and sorted by MTTR descending.
function MttrDetailedTable({ filters, dateRange }) {
  const filterKey = getMeanTimeToResolveDetailedScope(filters, dateRange);
  // Page is tied to the filter set it was chosen under, so changing filters returns to page 1
  // without firing a request for a stale page number.
  const [pageState, setPageState] = useState({ filterKey, page: 1 });
  const page = pageState.filterKey === filterKey ? pageState.page : 1;
  const goTo = useCallback(next => setPageState({ filterKey, page: next }), [filterKey]);

  const { data, loading, error } = useMeanTimeToResolveDetailed(filters, dateRange, page, PAGE_SIZE);
  const initialLoading = loading && !data;
  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;

  // If the result set shrinks below the current page, fall back to the last page instead of
  // leaving an empty table with the pager hidden. Skipped while loading: `data` is then the previous
  // page's response and its `total` may be out of date.
  useEffect(() => {
    if (data && !loading && page > Math.max(totalPages, 1)) goTo(Math.max(totalPages, 1));
  }, [data, loading, page, totalPages, goTo]);

  return (
    <>
      <div className="qk-chart-title">[MTTR] Mean Time to Resolve — Per Incident</div>
      {error && <div role="alert" className="section-error">Couldn't load per-incident MTTR.</div>}
      {initialLoading && <div className="section-loading">Loading…</div>}
      {!initialLoading && !error && (
        <>
          <div aria-busy={loading} style={{ opacity: loading ? 0.5 : 1 }}>
            <DataTable columns={COLUMNS} rows={rows} ariaLabel="Mean Time to Resolve per incident" />
          </div>
          {totalPages > 1 && (
            <div className="pagination-controls" style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px' }}>
              <button type="button" onClick={() => goTo(page - 1)} disabled={loading || page <= 1} style={{ marginRight: '8px', padding: '4px 8px' }}>
                ← Previous
              </button>
              <span style={{ margin: '0 8px' }}>Page {page} of {totalPages} ({total} total)</span>
              <button type="button" onClick={() => goTo(page + 1)} disabled={loading || page >= totalPages} style={{ marginLeft: '8px', padding: '4px 8px' }}>
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default MttrDetailedTable;
